import React, { createContext, useContext } from 'react';

/**
 * Lets a grid hear about trouble-ticket saves that happen deep inside a PRO
 * drawer it launched, so it can update the row the user opened from.
 *
 * The editor sits five levels down (ProDetailSlideOver → ProDetailContainer →
 * ProDetail → OrderFormView → TroubleTicketsPanel → TroubleTicketEditModal),
 * and a couple of those levels have nothing to do with tickets — threading a
 * callback through all of them would be noise. Context crosses the drawer's
 * `createPortal` boundary because it follows the React tree, not the DOM.
 *
 * Opt-in: with no provider the hook returns null and every editor behaves
 * exactly as it did before.
 */

/**
 * What a save actually wrote. The listener gets the effective (post-edit)
 * values so it can patch a row without re-reading the server.
 *
 * Called with no payload when the ticket itself didn't change but related data
 * did — the Reschedule Requested side effect — which is a reconcile-only signal.
 */
export interface TroubleTicketSaveResult {
  pronumber: number;
  /** recno of the saved ticket. Undefined only if a create response omitted it. */
  recno?: number;
  /** True for a create — a launching grid has no row for this ticket yet. */
  created: boolean;
  /** The ticket's own fields, as saved. */
  ticket: {
    category: string;
    status: string;
    assignedto: string;
    issue: string;
    resolve: string;
    preventable: string;
    driverticket: string;
    servicefailure: string;
  };
  /**
   * The embedded service failure (1:1 reschedule) as saved, or null when the SF
   * section wasn't written on this save.
   */
  serviceFailure: {
    servicefailuredate: string | null;
    reason: string;
    servicefailuretype: string;
    thd_reason: string;
    responsibledriver: string;
    comments: string;
  } | null;
}

export type TroubleTicketSaveListener = (saved?: TroubleTicketSaveResult) => void;

const TroubleTicketSaveContext = createContext<TroubleTicketSaveListener | null>(null);

export const TroubleTicketSaveProvider: React.FC<{
  onTicketSaved: TroubleTicketSaveListener;
  children: React.ReactNode;
}> = ({ onTicketSaved, children }) => (
  <TroubleTicketSaveContext.Provider value={onTicketSaved}>
    {children}
  </TroubleTicketSaveContext.Provider>
);

/** The listener to notify after a ticket save, or null when nobody is listening. */
export const useTroubleTicketSaveListener = (): TroubleTicketSaveListener | null =>
  useContext(TroubleTicketSaveContext);

export default TroubleTicketSaveContext;
